import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useParent } from '../../contexts/parentContext';
import ProfileImage from '../../components/profileImage';
import { feedService } from '../../services/api';
import api from '../../services/api';

const ChildControlFeed = () => {
  const { t } = useTranslation();
  const { selectedChild } = useParent();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Debug logging
  console.log('ChildControlFeed rendered with selectedChild:', selectedChild);
  
  useEffect(() => {
    console.log('useEffect triggered with selectedChild:', selectedChild);
    console.log('selectedChild._id:', selectedChild?._id);
    console.log('selectedChild.id:', selectedChild?.id);
    console.log('selectedChild.userId:', selectedChild?.userId);
    
    const userId = selectedChild?._id || selectedChild?.id || selectedChild?.userId;
    
    if (userId) {
      console.log('✅ Condition met with userId:', userId, 'calling fetchPosts');
      fetchPosts(1, true);
    } else {
      console.log('❌ Condition NOT met, no valid userId found');
      setLoading(false);
    }
  }, [selectedChild]);

  const fetchPosts = async (pageNum = 1, reset = false) => {
    console.log('📡 fetchPosts called with pageNum:', pageNum, 'reset:', reset);
    console.log('📡 selectedChild in fetchPosts:', selectedChild);
    
    try {
      if (reset) {
        setLoading(true);
        setError('');
      } else {
        setLoadingMore(true);
      }

      // Try different possible user ID fields
      const userId = selectedChild._id || selectedChild.id || selectedChild.userId;
      
      if (!userId) {
        throw new Error('No valid user ID found for selected child');
      }

      console.log('Fetching posts for user:', userId, 'page:', pageNum);
      console.log('Selected child object:', selectedChild);
      
      console.log('Selected child roles:', selectedChild.role);
      
      // Check if parent has feed permission for this child
      const hasFeedPermission = selectedChild.role && selectedChild.role.includes('feed');
      console.log('Parent has feed permission:', hasFeedPermission);
      
      if (!hasFeedPermission) {
        throw new Error(`Feed monitoring not enabled for ${selectedChild.userName}. Please contact the child to grant feed monitoring permission.`);
      }
      
      // TRY THE ORIGINAL WORKING APPROACH
      // If this worked before, let's try different endpoints to find the working one
      
      // Use only the correct endpoint with proper data
      console.log('📡 Making API call to /feed/get-all-user-feed-by-user-id');
      console.log('📡 Parameters: userId =', userId, 'page =', pageNum);
      
      const response = await feedService.getUserPosts(userId, pageNum);
      
      // Handle the backend response format
      if (response.data?.success) {
        const data = response.data.data;
        const newPosts = data?.posts || [];
        const pagination = data?.pagination;
        
        console.log('Posts received:', newPosts.length);
        console.log('Pagination:', pagination);
        
        if (reset) {
          setPosts(newPosts);
        } else {
          setPosts(prev => [...prev, ...newPosts]);
        }
        
        setHasMore(pagination?.hasMore || pagination?.hasNext || false);
        setPage(pageNum);
      } else {
        throw new Error(response.data?.error || response.data?.message || 'Failed to fetch posts');
      }
    } catch (err) {
      console.error('Error fetching posts:', err);
      console.error('Error response:', err.response?.data);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to load posts';
      if (err.response?.status === 404) {
        errorMessage = 'User not found or no posts available';
      } else if (err.response?.status === 401) {
        errorMessage = 'Authentication required';
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      if (reset) {
        setPosts([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm(t('common.confirmDelete'))) return;
    
    try {
      setDeleting(postId);
      console.log('Deleting post:', postId);
      
      const response = await feedService.deletePost(postId);
      console.log('Delete response:', response.data);
      
      // Handle success response
      if (response.data?.success !== false) {
        setPosts(prev => prev.filter(post => (post.postId || post._id) !== postId));
        console.log('Post deleted successfully');
      } else {
        throw new Error(response.data?.error || response.data?.message || 'Failed to delete post');
      }
    } catch (err) {
      console.error('Error deleting post:', err);
      console.error('Delete error response:', err.response?.data);
      
      let errorMessage = 'Failed to delete post';
      if (err.response?.status === 404) {
        errorMessage = 'Post not found';
      } else if (err.response?.status === 403) {
        errorMessage = 'Not authorized to delete this post';
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setDeleting(null);
    }
  };

  const loadMorePosts = () => {
    if (hasMore && !loadingMore) {
      fetchPosts(page + 1, false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!selectedChild) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('parent.noChildSelected')}</h3>
          <p className="text-gray-500 mb-4">{t('parent.selectChildFirst')}</p>
          <Link
            to="/parent/child-control"
            className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            {t('parent.selectChild')}
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">{t('parent.loadingFeed')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl shadow-xl overflow-hidden mb-8">
          <div className="p-8 text-white">
            <div className="flex items-center space-x-6">
              <ProfileImage
                image={selectedChild.profilePicture}
                alt={selectedChild.firstName}
                className="w-16 h-16 rounded-full object-cover border-4 border-white/20"
              />
              <div>
                <h1 className="text-3xl font-bold mb-2">
                  {selectedChild.firstName}'s {t('parent.feed')}
                </h1>
                <p className="text-blue-100 text-lg">{t('parent.feedMonitoring')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6">
            <div className="flex items-center justify-between">
              <span>{error}</span>
              <button 
                onClick={() => setError('')}
                className="text-red-500 hover:text-red-700"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Posts Feed */}
        <div className="space-y-6">
          {posts.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z"/>
                </svg>
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-2">{t('parent.noPosts')}</h4>
              <p className="text-gray-500">{t('parent.noPostsDescription')}</p>
            </div>
          ) : (
            <>
              {posts.map((post) => (
                <div key={post.postId || post._id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <ProfileImage
                        image={post.user?.profilePicture || selectedChild.profilePicture}
                        alt={post.user?.userName || selectedChild.firstName}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">
                          {post.user?.firstName || selectedChild.firstName} {post.user?.lastName || selectedChild.lastName}
                        </h4>
                        <p className="text-sm text-gray-500">
                          {post.timeAgo || formatDate(post.createdAt)}
                        </p>
                      </div>
                    </div>
                    
                    {/* Delete button for parent control */}
                    <button
                      onClick={() => handleDeletePost(post.postId || post._id)}
                      disabled={deleting === (post.postId || post._id)}
                      className="text-red-500 hover:text-red-700 p-2 rounded-full hover:bg-red-50 transition-colors disabled:opacity-50"
                      title={t('common.delete')}
                    >
                      {deleting === (post.postId || post._id) ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-red-500 border-t-transparent"></div>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      )}
                    </button>
                  </div>
                  
                  {/* Post Content */}
                  <div className="mb-4">
                    <p className="text-gray-800 text-lg leading-relaxed whitespace-pre-wrap">{post.content}</p>
                  </div>

                  {/* Post Image */}
                  {post.image && (
                    <div className="mb-4">
                      <img
                        src={post.image}
                        alt="Post content"
                        className="w-full max-h-96 object-cover rounded-lg"
                      />
                    </div>
                  )}

                  {/* Post Stats & Actions */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center space-x-6 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"/>
                        </svg>
                        <span>{post.likesCount || 0} {t('parent.likes')}</span>
                      </div>
                      
                      <div className="flex items-center space-x-1">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z"/>
                        </svg>
                        <span>{post.commentsCount || 0} {t('parent.comments')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Load More Button */}
              {hasMore && (
                <div className="text-center">
                  <button
                    onClick={loadMorePosts}
                    disabled={loadingMore}
                    className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingMore ? (
                      <div className="flex items-center space-x-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                        <span>{t('common.loading')}</span>
                      </div>
                    ) : (
                      t('feed.loadMore')
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChildControlFeed; 